import React, { useState, useEffect, useCallback } from "react";
import "../../assets/css/nlis.css";
import CustomAlert from "../general/customAlert";
import CustomTable from "../general/CustomTable";
import { getPicExceptions, getDeviceExceptions, refreshPicExceptions } from "../../api/nlis";

const picColumns = [
  { name: "PIC", value: "pic" },
  { name: "Exception Status", value: "exception_status" },
  { name: "Program Code", value: "program_code" },
  { name: "Setting", value: "setting" },
];

const deviceColumns = [
  { name: "RF Identity", value: "rf_identity" },
  { name: "NLIS Identity", value: "nlis_identity" },
  { name: "Exception Status", value: "exception_status" },
  { name: "Program Code", value: "program_code" },
  { name: "Setting", value: "setting" },
];

const ERPExceptionsPage = () => {
  const [activeTab, setActiveTab] = useState("pic");

  // PIC tab state
  const [picData, setPicData] = useState([]);
  const [picTotal, setPicTotal] = useState(0);
  const [picPage, setPicPage] = useState(1);
  const [picTotalPages, setPicTotalPages] = useState(0);
  const [picSearch, setPicSearch] = useState("");
  const [picSearchInput, setPicSearchInput] = useState("");
  const [picLoading, setPicLoading] = useState(false);
  const [picVisibleColumns, setPicVisibleColumns] = useState({});

  // Device tab state
  const [deviceData, setDeviceData] = useState([]);
  const [deviceTotal, setDeviceTotal] = useState(0);
  const [devicePage, setDevicePage] = useState(1);
  const [deviceTotalPages, setDeviceTotalPages] = useState(0);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [deviceSearchInput, setDeviceSearchInput] = useState("");
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [deviceVisibleColumns, setDeviceVisibleColumns] = useState({});

  // Refresh state
  const [refreshing, setRefreshing] = useState(false);

  // Shared alert state
  const [isMsg, setIsMsg] = useState(false);
  const [msg, setMsg] = useState(null);
  const [isError, setIsError] = useState(false);

  const loadPicExceptions = useCallback(async (page, search) => {
    setPicLoading(true);
    try {
      const res = await getPicExceptions({ page, search });
      const { data, total, totalPages } = res.data;
      setPicData(data);
      setPicTotal(total);
      setPicTotalPages(totalPages);
      setPicVisibleColumns(
        picColumns.reduce((acc, col) => {
          acc[col.name] = true;
          return acc;
        }, {})
      );
    } catch (err) {
      console.error("[ERP Exceptions] PIC load error:", err);
      setIsError(true);
      setIsMsg(true);
      setMsg("Failed to load PIC exceptions.");
    } finally {
      setPicLoading(false);
    }
  }, []);

  const loadDeviceExceptions = useCallback(async (page, search) => {
    setDeviceLoading(true);
    try {
      const res = await getDeviceExceptions({ page, search });
      const { data, total, totalPages } = res.data;
      setDeviceData(data);
      setDeviceTotal(total);
      setDeviceTotalPages(totalPages);
      setDeviceVisibleColumns(
        deviceColumns.reduce((acc, col) => {
          acc[col.name] = true;
          return acc;
        }, {})
      );
    } catch (err) {
      console.error("[ERP Exceptions] Device load error:", err);
      setIsError(true);
      setIsMsg(true);
      setMsg("Failed to load device exceptions.");
    } finally {
      setDeviceLoading(false);
    }
  }, []);

  // Load PIC data on mount and when page/search changes
  useEffect(() => {
    loadPicExceptions(picPage, picSearch);
  }, [picPage, picSearch, loadPicExceptions]);

  // Load Device data when tab switches to device or when page/search changes
  useEffect(() => {
    if (activeTab === "device" || deviceData.length === 0) {
      loadDeviceExceptions(devicePage, deviceSearch);
    }
  }, [devicePage, deviceSearch]);

  // Load device data when switching to that tab for the first time
  useEffect(() => {
    if (activeTab === "device" && deviceData.length === 0) {
      loadDeviceExceptions(1, "");
    }
  }, [activeTab]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await refreshPicExceptions();
      const { inserted, skipped } = res.data;
      setIsError(false);
      setIsMsg(true);
      setMsg(`[NLIS PIC Exceptions] Inserted: ${inserted}, Skipped (already exist): ${skipped}`);
      // Reload current PIC tab data
      setPicPage(1);
      setPicSearch("");
      setPicSearchInput("");
      await loadPicExceptions(1, "");
    } catch (err) {
      console.error("[ERP Exceptions] Refresh error:", err);
      setIsError(true);
      setIsMsg(true);
      setMsg("Failed to refresh ERP exceptions. Please try again.");
    } finally {
      setRefreshing(false);
    }
  };

  const handlePicSearch = () => {
    setPicPage(1);
    setPicSearch(picSearchInput.trim());
  };

  const handleDeviceSearch = () => {
    setDevicePage(1);
    setDeviceSearch(deviceSearchInput.trim());
  };

  const handlePicClear = () => {
    setPicSearchInput("");
    setPicPage(1);
    setPicSearch("");
  };

  const handleDeviceClear = () => {
    setDeviceSearchInput("");
    setDevicePage(1);
    setDeviceSearch("");
  };

  const renderPagination = (currentPage, totalPages, onPageChange) => {
    if (totalPages <= 1) return null;

    const maxButtons = 7;
    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);
    if (endPage - startPage < maxButtons - 1) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }
    const pages = [];
    for (let i = startPage; i <= endPage; i++) pages.push(i);

    return (
      <div className="pagination mt-2">
        <button
          className="btn btn-acumen-color text-white me-2"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}>
          Previous
        </button>
        {startPage > 1 && (
          <>
            <button className="btn btn-light me-1" onClick={() => onPageChange(1)}>1</button>
            {startPage > 2 && <span className="me-1 align-self-center">…</span>}
          </>
        )}
        {pages.map((p) => (
          <button
            key={p}
            className={`btn ${currentPage === p ? "btn-secondary" : "btn-light"} me-1`}
            onClick={() => onPageChange(p)}>
            {p}
          </button>
        ))}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="me-1 align-self-center">…</span>}
            <button className="btn btn-light me-1" onClick={() => onPageChange(totalPages)}>{totalPages}</button>
          </>
        )}
        <button
          className="btn btn-acumen-color text-white ms-2"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}>
          Next
        </button>
      </div>
    );
  };

  const renderSearchBar = (inputValue, setInputValue, onSearch, onClear, placeholder) => (
    <div className="d-flex gap-2 mb-3" style={{ maxWidth: "480px" }}>
      <input
        type="text"
        className="form-control"
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onSearch()}
      />
      <button className="btn btn-acumen-color text-white" onClick={onSearch}>
        Search
      </button>
      {inputValue && (
        <button className="btn btn-outline-secondary" onClick={onClear}>
          Clear
        </button>
      )}
    </div>
  );

  return (
    <div className="nlis-page erp-exceptions-page">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0 text-acumen-color">NLIS ERP Exceptions</h4>
        <button
          className="btn btn-acumen-color text-white"
          onClick={handleRefresh}
          disabled={refreshing}>
          {refreshing ? "Refreshing…" : "↻ Refresh"}
        </button>
      </div>

      <div className="container-fluid">
        {/* Tabs */}
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "pic" ? "active" : ""}`}
              onClick={() => setActiveTab("pic")}>
              PIC Exceptions
              {picTotal > 0 && (
                <span className="badge bg-secondary ms-2">{picTotal.toLocaleString()}</span>
              )}
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "device" ? "active" : ""}`}
              onClick={() => setActiveTab("device")}>
              Device Exceptions
              {deviceTotal > 0 && (
                <span className="badge bg-secondary ms-2">{deviceTotal.toLocaleString()}</span>
              )}
            </button>
          </li>
        </ul>

        {/* PIC Exceptions Tab */}
        {activeTab === "pic" && (
          <div>
            {renderSearchBar(
              picSearchInput,
              setPicSearchInput,
              handlePicSearch,
              handlePicClear,
              "Search by PIC, status or program code..."
            )}

            {picLoading && (
              <div className="alert alert-info mt-2">Loading PIC exceptions...</div>
            )}

            {!picLoading && picData.length > 0 && (
              <>
                <div className="text-muted mb-2" style={{ fontSize: "0.875rem" }}>
                  Showing {((picPage - 1) * 50) + 1}–{Math.min(picPage * 50, picTotal)} of {picTotal.toLocaleString()} records
                  {picSearch && ` matching "${picSearch}"`}
                </div>
                <CustomTable
                  items={picData}
                  columns={picColumns}
                  visibleColumns={picVisibleColumns}
                  setVisibleColumns={setPicVisibleColumns}
                  cantHideColumns={["pic"]}
                  maxHeight="calc(100vh - 360px)"
                  horizontalMargin="mx-1"
                />
                {renderPagination(picPage, picTotalPages, setPicPage)}
              </>
            )}

            {!picLoading && picData.length === 0 && !isError && (
              <div className="alert alert-warning mt-2">
                {picSearch ? `No PIC exceptions found matching "${picSearch}".` : "No PIC exceptions found."}
              </div>
            )}
          </div>
        )}

        {/* Device Exceptions Tab */}
        {activeTab === "device" && (
          <div>
            {renderSearchBar(
              deviceSearchInput,
              setDeviceSearchInput,
              handleDeviceSearch,
              handleDeviceClear,
              "Search by RF identity, NLIS identity, status or program code..."
            )}

            {deviceLoading && (
              <div className="alert alert-info mt-2">Loading device exceptions...</div>
            )}

            {!deviceLoading && deviceData.length > 0 && (
              <>
                <div className="text-muted mb-2" style={{ fontSize: "0.875rem" }}>
                  Showing {((devicePage - 1) * 50) + 1}–{Math.min(devicePage * 50, deviceTotal)} of {deviceTotal.toLocaleString()} records
                  {deviceSearch && ` matching "${deviceSearch}"`}
                </div>
                <CustomTable
                  items={deviceData}
                  columns={deviceColumns}
                  visibleColumns={deviceVisibleColumns}
                  setVisibleColumns={setDeviceVisibleColumns}
                  cantHideColumns={["rf_identity"]}
                  maxHeight="calc(100vh - 360px)"
                  horizontalMargin="mx-1"
                />
                {renderPagination(devicePage, deviceTotalPages, setDevicePage)}
              </>
            )}

            {!deviceLoading && deviceData.length === 0 && !isError && (
              <div className="alert alert-warning mt-2">
                {deviceSearch ? `No device exceptions found matching "${deviceSearch}".` : "No device exceptions found."}
              </div>
            )}
          </div>
        )}
      </div>

      {isMsg && (
        <CustomAlert
          isError={isError}
          message={msg}
          showMessage={isMsg}
          setShowMessage={setIsMsg}
        />
      )}
    </div>
  );
};

export default ERPExceptionsPage;